import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Employees.css';

function Employees() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/time-tracking/employees', {
        params: {
          search: searchTerm.trim(),
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setEmployees(response.data.employees || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load employees.');
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    const timeoutId = setTimeout(fetchEmployees, 200);
    return () => clearTimeout(timeoutId);
  }, [fetchEmployees]);

  const emptyText = useMemo(() => {
    if (searchTerm.trim()) {
      return 'Change filters or press the (+) button to create a new record';
    }

    return 'Press the (+) button to create a new record';
  }, [searchTerm]);

  return (
    <div className="employees-page">
      <div className="employees-side-handle">{'\u2039'}</div>

      <header className="employees-header">
        <button className="employees-back" onClick={() => navigate('/time-tracking')}>{'\u2190'}</button>
        <div>
          <h1>Employees</h1>
          <p>
            Keep track of all employees who have particular roles in your organization.
            <br />
            You can also assign them to jobs and tasks on the JobCard page
          </p>
        </div>
      </header>

      <div className="employees-search-wrap">
        <span className="employees-search-icon" aria-hidden="true" />
        <input
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder="Search by name, phone or role"
        />
      </div>

      {loading ? (
        <div className="employees-state">Loading employees...</div>
      ) : error ? (
        <div className="employees-state error">{error}</div>
      ) : employees.length === 0 ? (
        <div className="employees-empty">
          <div className="empty-document">
            <div className="empty-document-page">
              <div className="empty-fold" />
              <div className="empty-face">
                <span />
                <span />
              </div>
              <div className="empty-mouth" />
            </div>
            <div className="empty-magnifier" />
          </div>
          <h2>No Employees Found</h2>
          <p>{emptyText}</p>
        </div>
      ) : (
        <div className="employees-table-wrap">
          <table className="employees-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Role</th>
                <th>Starting Date</th>
                <th>Salary</th>
                <th>Rate/Hour</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {employees.map((employee) => (
                <tr key={employee.EmployeeId}>
                  <td>{employee.FullName}</td>
                  <td>{employee.PhoneNumber}</td>
                  <td>{employee.Email || '-'}</td>
                  <td>{employee.RoleName || '-'}</td>
                  <td>{employee.StartingDate ? new Date(employee.StartingDate).toLocaleDateString() : '-'}</td>
                  <td>{employee.Salary ?? '-'}</td>
                  <td>{employee.RatePerHour ?? '-'}</td>
                  <td>{employee.IsActive ? 'Active' : 'Inactive'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <button className="new-employee-button" onClick={() => navigate('/time-tracking/employees/new')}>
        <span aria-hidden="true">+</span>
        New Employee
      </button>
    </div>
  );
}

export default Employees;
